import { format, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import TIMEZONES from '@shared/timezones';

/**
 * Converts Rails timezone name to IANA timezone identifier
 * @param railsTimezone - Rails timezone name (e.g., "Eastern Time (US & Canada)")
 * @returns IANA timezone identifier (e.g., "America/New_York")
 */
export function convertRailsToIANATimezone(railsTimezone: string): string {
  // If it's already an IANA timezone (contains /), return as is
  if (railsTimezone && railsTimezone.includes('/')) {
    return railsTimezone;
  }
  
  // Find matching timezone in our list
  const timezone = TIMEZONES.find(tz => tz.label === railsTimezone);
  
  // Return the IANA value if found, otherwise default to UTC
  return timezone?.value || 'UTC';
}

/**
 * Formats a date/time string in a specific timezone
 * @param dateString - ISO date string (can be with or without timezone)
 * @param timezone - IANA timezone string or Rails timezone name
 * @param formatString - date-fns format string
 * @returns Formatted date string in the specified timezone
 */
export function formatDateInTimezone(
  dateString: string | Date,
  timezone: string,
  formatString: string = 'MMM d, yyyy h:mm a'
): string {
  if (!dateString) return '';
  
  try {
    // Convert Rails timezone to IANA if needed
    const ianaTimezone = convertRailsToIANATimezone(timezone);
    
    // If it's already a Date object, convert to ISO string
    const dateStr = dateString instanceof Date ? dateString.toISOString() : dateString;
    return formatInTimeZone(dateStr, ianaTimezone, formatString);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return '';
  }
}

/**
 * Formats a date/time with timezone abbreviation
 * @param dateString - ISO date string
 * @param timezone - IANA timezone string
 * @param includeYear - Whether to include the year in the format
 * @returns Formatted date string with timezone abbreviation
 */
export function formatDateWithTimezone(
  dateString: string | Date,
  timezone: string,
  includeYear: boolean = true
): string {
  if (!dateString) return '';
  
  const formatString = includeYear ? 'MMM d, yyyy h:mm a zzz' : 'MMM d, h:mm a zzz';
  return formatDateInTimezone(dateString, timezone, formatString);
}

/**
 * Gets the timezone abbreviation for a given timezone and date
 * @param timezone - IANA timezone string
 * @param date - Date to get abbreviation for (defaults to now)
 * @returns Timezone abbreviation (e.g., "EST", "PDT")
 */
export function getTimezoneAbbreviation(
  timezone: string,
  date: Date | string = new Date()
): string {
  try {
    // Convert Rails timezone to IANA if needed
    const ianaTimezone = convertRailsToIANATimezone(timezone);
    
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    const formatted = formatInTimeZone(dateStr, ianaTimezone, 'zzz');
    return formatted;
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return '';
  }
}

/**
 * Formats just the time portion in a specific timezone
 * @param dateString - ISO date string
 * @param timezone - IANA timezone string
 * @returns Formatted time string (e.g., "2:00 PM")
 */
export function formatTimeInTimezone(
  dateString: string | Date,
  timezone: string
): string {
  return formatDateInTimezone(dateString, timezone, 'h:mm a');
}

/**
 * Checks if the event timezone is different from the user's local timezone
 * @param eventTimezone - IANA timezone string for the event
 * @returns true if timezones are different
 */
export function isTimezoneDifferent(eventTimezone: string): boolean {
  try {
    // Convert Rails timezone to IANA if needed
    const ianaTimezone = convertRailsToIANATimezone(eventTimezone);
    
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return userTimezone !== ianaTimezone;
  } catch (error) {
    console.error('Error comparing timezones:', error);
    return false;
  }
}

/**
 * Formats a date for display with optional timezone info
 * @param dateString - ISO date string
 * @param eventTimezone - IANA timezone string for the event
 * @param showTimezone - Whether to show timezone abbreviation
 * @param formatString - Custom format string (optional)
 * @returns Formatted date string
 */
export function formatEventDate(
  dateString: string | Date,
  eventTimezone: string,
  showTimezone: boolean = true,
  formatString?: string
): string {
  if (!dateString || !eventTimezone) return '';
  
  const shouldShowTimezone = showTimezone && isTimezoneDifferent(eventTimezone);
  const baseFormat = formatString || 'MMM d, yyyy h:mm a';
  const finalFormat = shouldShowTimezone ? `${baseFormat} zzz` : baseFormat;
  
  return formatDateInTimezone(dateString, eventTimezone, finalFormat);
}

/**
 * Formats a short date (no year) for display with optional timezone
 * @param dateString - ISO date string
 * @param eventTimezone - IANA timezone string for the event
 * @param showTimezone - Whether to show timezone abbreviation
 * @returns Formatted date string
 */
export function formatEventDateShort(
  dateString: string | Date,
  eventTimezone: string,
  showTimezone: boolean = true
): string {
  return formatEventDate(dateString, eventTimezone, showTimezone, 'MMM d');
}